import { webAuthnError, webAuthnUnsupportedError } from 'client/services/response/WebAuthnErrors';
import { webAuthnSupported } from 'client/util/FeatureSupport';
import UserLoginApi from 'client/api/UserLoginApi';
import webAuthnFormatting from 'client/services/webAuthnFormatting';

export default {
  getCredentials(...props) {
    return navigator.credentials.get(...props);
  },

  async getChallenge(challenge) {
    try {
      const { publicKey, token } = challenge || await UserLoginApi.getWebAuthnChallenge();

      return {
        publicKey: webAuthnFormatting.encodePublicKey(publicKey),
        token,
      };
    } catch (error) {
      throw webAuthnError;
    }
  },

  // ID First TODO: Delete once password reset is implemented
  async postSignature(publicKeyCredential, token, passwordResetInfo) {
    const decodedPublicKeyCredential = webAuthnFormatting
      .decodePublicKeyCredential(publicKeyCredential);

    const payload = {
      publicKeyCredential: decodedPublicKeyCredential,
      token,
    };

    if (passwordResetInfo && passwordResetInfo.confirmationCode) {
      return UserLoginApi.resetPassword(
        passwordResetInfo.confirmationCode,
        {
          ...passwordResetInfo,
          ...payload,
        },
      );
    }

    return UserLoginApi.postWebAuthnSignature(payload);
  },

  // ID First TODO: Delete once password reset is implemented
  async authenticate(passwordResetInfo, challenge) {
    if (!webAuthnSupported()) throw new Error('This browser does not support Security Keys.');

    try {
      // Get challenge from the api
      const { publicKey, token } = await this.getChallenge(challenge);

      // Get the signed Credential from the key
      const options = {
        publicKey,
      };
      const credential = await this.getCredentials(options);

      // Verify the credential via the api
      return this.postSignature(
        credential,
        token,
        passwordResetInfo,
      );
    } catch {
      throw new Error('Failed to verify security key.');
    }
  },

  // New methods to separate login from password reset
  async prepareLoginRequest() {
    if (!webAuthnSupported()) throw webAuthnUnsupportedError;

    try {
      // Get challenge from the api
      const { publicKey, token } = await this.getChallenge();

      // Get the signed Credential from the key
      const credential = await this.getCredentials({ publicKey });
      const publicKeyCredential = webAuthnFormatting.decodePublicKeyCredential(credential);

      return {
        publicKeyCredential,
        token,
      };
    } catch {
      throw webAuthnError;
    }
  },

  async preparePasswordResetRequest(challenge = {}) {
    if (!webAuthnSupported()) throw webAuthnUnsupportedError;

    try {
      const { publicKey, token } = challenge;
      const credential = await this.getCredentials({
        publicKey: webAuthnFormatting.encodePublicKey(publicKey),
      });

      return {
        publicKeyCredential: webAuthnFormatting.decodePublicKeyCredential(credential),
        token,
      };
    } catch {
      throw webAuthnError;
    }
  },
};
